import { useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PoliticalCampaignsApi } from '@/services/political/politicalCampaignsApi'
import '../../styles/public-campaign-info.css'
import { getLocale } from '../../utils/locale'

export function PublicCampaignInfoPage() {
  const { campaignId } = useParams<{ campaignId: string }>()
  const { t } = useTranslation()

  const { data: campaign, isLoading, error } = useQuery({
    queryKey: ['publicCampaign', campaignId],
    queryFn: () => PoliticalCampaignsApi.getPublicById(campaignId!),
    enabled: !!campaignId,
  })

  if (isLoading) {
    return (
      <div className="public-campaign-page">
        <div className="campaign-container">
          <div className="not-found">
            <p>{t('political.publicPage.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="public-campaign-page">
        <div className="campaign-container">
          <div className="not-found">
            <h1>{t('political.publicPage.notFoundTitle')}</h1>
            <p>{t('political.publicPage.notFoundBody')}</p>
          </div>
        </div>
      </div>
    )
  }

  const fmt = (n: number) =>
    new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency: campaign.currency,
      maximumFractionDigits: 0,
    }).format(n)

  return (
    <div className="public-campaign-page">
      <div className="campaign-container">
        <header className="campaign-header-public">
          <h1>{t('political.publicPage.whyThisAd')}</h1>
          <p className="subtitle">{t('political.publicPage.transparency')}</p>
        </header>

        <main className="campaign-content">
          <section className="campaign-section">
            <h2>{t('political.publicPage.campaignDetails')}</h2>
            <div className="info-grid">
              <div className="info-item">
                <h3>{t('common.name')}</h3>
                <p>{campaign.name}</p>
              </div>
              {campaign.description && (
                <div className="info-item full-width">
                  <h3>{t('common.description')}</h3>
                  <p>{campaign.description}</p>
                </div>
              )}
              <div className="info-item">
                <h3>{t('political.totalBudget')}</h3>
                <p>{fmt(campaign.totalBudget)}</p>
              </div>
              <div className="info-item">
                <h3>{t('political.election')}</h3>
                <p>{campaign.electionName || '—'}</p>
              </div>
            </div>
          </section>

          <section className="campaign-section">
            <h2>{t('political.publicPage.politicalEntity')}</h2>
            <div className="info-grid">
              <div className="info-item">
                <h3>{t('common.name')}</h3>
                <p>{campaign.politicalEntityName || '—'}</p>
              </div>
            </div>
          </section>

          {campaign.topics.length > 0 && (
            <section className="campaign-section">
              <h2>{t('political.publicPage.budgetAllocation')}</h2>
              <div className="topics-list-public">
                {campaign.topics.map((topic) => (
                  <div key={topic.id} className="topic-item">
                    <div className="topic-name">{topic.name}</div>
                    <div className="topic-budget">{fmt(topic.amount)}</div>
                    <div className="topic-percentage">
                      {campaign.totalBudget > 0
                        ? Math.round((topic.amount / campaign.totalBudget) * 100)
                        : 0}{' '}
                      {t('political.publicPage.percentOfTotal')}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="campaign-footer">
            <p className="footer-text">
              {t('political.publicPage.footer')}
            </p>
          </section>
        </main>
      </div>
    </div>
  )
}
